import { defineStore } from 'pinia'
import PartnersLib from '~/libs/portal-partners'
import { localStorageService } from '~/libs/local-storage.service'
import modalContent from '~/libs/modal-helper'
import {
  DAYS,
  INTELLIGENT_LENDING_PORTAL_NAV_TITLES,
  PORTAL_APP_STATE,
  PORTAL_NAV_TITLES,
  PORTAL_NAV_URLS,
  EMBEDDED_NAV_LINKS,
  INTELLIGENT_LENDING_NAV_LINKS,
  BRANDED_NAV_LINKS,
  PORTAL_NAV_LINKS,
} from '~/libs/portal-constants'
import { AFFILIATE_TYPES } from '~/libs/embedded'
import env from '~/libs/env'

import { useApprovalsStore } from '~/store/approvals'
import { useBorrowerStore } from '~/store/borrower'
import { useDealsStore } from '~/store/deals'
import { useDocumentsStore } from '~/store/documents'
import { useExperienceStore } from '~/store/experience'
import { useProgressStore } from '~/store/progress'
import { useUserStore } from '~/store/user'
import { useAffiliateCustomizationStore } from '~/store/affiliate-customization'
import { useApplicationConfigStore } from '~/store/application-config'
import { useProgressCardStore } from '~/store/progress-card'
import { useOfferOptionsStore } from '~/store/offer-options'
import { useRootStore } from '~/store/root'
import { ContactsService } from '~/libs/store-services/contacts-service'
import { phone } from '~/libs/format'
import { get } from 'es-toolkit/compat'

// Helper functions Getters
function checkDaysSinceAppComplete(appComplete) {
  if (appComplete) {
    const now = new Date().getTime() / DAYS
    const appCompleteTime = new Date(appComplete).getTime() / DAYS
    if (appCompleteTime) {
      return now - appCompleteTime
    }
  }
  return null
}

function checkAppCompleteInLast30Days(appComplete) {
  if (appComplete) {
    const daysSinceAppComplete = checkDaysSinceAppComplete(appComplete)
    return !daysSinceAppComplete || daysSinceAppComplete <= 30
  }
  return false
}
// End helper functions

export const usePortalStore = defineStore('portal', () => {
  const nuxtApp = useNuxtApp()
  const { $axios, $axiosWithRetry } = nuxtApp

  const approvalsStore = useApprovalsStore()
  const borrowerStore = useBorrowerStore()
  const dealsStore = useDealsStore()
  const documentsStore = useDocumentsStore()
  const experienceStore = useExperienceStore()
  const progressStore = useProgressStore()
  const userStore = useUserStore()
  const affiliateCustomizationStore = useAffiliateCustomizationStore()
  const appConfigStore = useApplicationConfigStore()
  const progressCardStore = useProgressCardStore()
  const offerOptionsStore = useOfferOptionsStore()
  const rootStore = useRootStore()
  const contactsService = new ContactsService($axios, $axiosWithRetry)

  /*
    ███████ ████████  █████  ████████ ███████
    ██         ██    ██   ██    ██    ██
    ███████    ██    ███████    ██    █████
         ██    ██    ██   ██    ██    ██
    ███████    ██    ██   ██    ██    ███████
    STATE
  */
  const navLinks = ref(PORTAL_NAV_LINKS)
  const embeddedNavLinks = ref(EMBEDDED_NAV_LINKS)
  const intelligentLendingNavLinks = ref(INTELLIGENT_LENDING_NAV_LINKS)
  const brandedNavLinks = ref(BRANDED_NAV_LINKS)
  const leadSourceRestricted = ref(false)
  const wywLinks = ref([{
      title: 'Explore Lendio business resources',
      url: '/portal/business-resources',
      icon: 'icon-list'
    },
    {
      title: 'Learn more ways to use a line of credit during the pandemic',
      url: 'https://www.lendio.com/blog/line-credit-during-pandemic/',
      icon: 'icon-file'
    },
    {
      title: 'Still have questions? FAQs',
      url: 'https://answers.lendio.com/home',
      icon: 'icon-question'
    }
  ])

  const currentDraw = ref('First Draw')
  const readyCapitalAppNumber = ref(null)
  const partners = ref(PartnersLib.partners)
  const portalLoaded = ref(false)
  const actionItems = ref({})
  const actionItemsTest = ref(false)
  const dnqCta = ref(true)
  const sbaResumeUrl = ref('')
  const shouldFetchResumeSBAUrl = ref(true)
  const shouldGetOwnersAccessCardData = ref(true)
  const additionalOwnerData = ref(null)
  const shouldDisplayAdditionalOwnersAccessCard = ref(false)
  const shouldGetOwnersClosingDocumentsCardData = ref(true)
  const additionalOwnersClosingDocumentsData = ref(null)
  const shouldDisplayAdditionalOwnersClosingDocumentsCard = ref(false)
  const showAlertBanner = ref(false)
  const alertBannerType = ref('info')
  const alertBannerMessage = ref(null)
  const alertBannerTitle = ref(null)
  const alertBannerCloseable = ref(false)
  const alertBannerShowIcon = ref(true)
  const alertBannerShowShadow = ref(false)
  const alertBannerLink = ref(null)
  const alertBannerLinkText = ref(null)
  const alertBannerLabel = ref(null)

/*
   ██████  ███████ ████████ ████████ ███████ ██████  ███████
  ██       ██         ██       ██    ██      ██   ██ ██
  ██   ███ █████      ██       ██    █████   ██████  ███████
  ██    ██ ██         ██       ██    ██      ██   ██      ██
   ██████  ███████    ██       ██    ███████ ██   ██ ███████
  GETTERS
*/
  const showDNQCta = computed(() => {
    const isDNQ = experienceStore.isDNQ
    const mineralGroup = get(
      borrowerStore,
      'borrower.mineralGroup'
    )

    if (
      !dnqCta.value ||
      !userStore.hasOptimusUser ||
      !isDNQ ||
      mineralGroup !== 'Coal'
    )
      return false
    const sixMonthsAgo = new Date() - 180 * 24 * 60 * 60 * 1000
    const applications = progressStore.sortedApplications
      .filter((app) =>
        ['marketplace', 'renewal', 'dnqReturn'].includes(app.type)
      )
      .concat([
        {
          started: get(
            borrowerStore,
            'borrowerValues.marketplaceAppStarted.value',
            0
          ),
          completed: get(
            borrowerStore,
            'borrowerValues.marketplaceAppCompleted.value',
            0
          )
        }
      ])
      .map((app) => ({
        started: new Date(app.started || 0),
        completed: new Date(app.completed || 0)
      }))
    const currentDnqApp = progressStore.sortedApplications.find((app) => app.type == 'dnqReturn')
    const recent = applications.reduce(
      (recent, app) => ({
        started: recent.started > app.started ? recent.started : app.started,
        completed:
          recent.completed > app.completed ? recent.completed : app.completed
      }),
      { started: new Date(0), completed: new Date(0) }
    )

    // If they've completed in the last six months, it should count
    if (recent.completed > sixMonthsAgo) return false

    const overSixMonthsOld = recent.started < sixMonthsAgo
    const afterDefaultDate = recent.started > new Date(0)
    const prevDnqNotComplete = currentDnqApp && !currentDnqApp.completed
    const prevDnqOverSixMonthsOld =
      currentDnqApp && currentDnqApp.completed < sixMonthsAgo

    return (
      (overSixMonthsOld && afterDefaultDate) ||
      prevDnqNotComplete ||
      prevDnqOverSixMonthsOld
    )
  })

  const partnerByName = computed(() => {
    return (name) => {
      return partners.value.find((_partners) => _partners.name === name)
    }
  })

  const isQualifiedBorrower = computed(() => {
    const QUALIFIED_CREDIT_THRESHOLD = 550
    const QUALIFIED_REVENUE_THRESHOLD = 8000

    const borrower = get(borrowerStore, 'borrower')
    const borrowerValues = get(
      borrowerStore,
      'borrowerValues'
    )
    if (borrower === null || borrowerValues === null) {
      return null
    }

    const hasRepAssigned = experienceStore.hasRepAssigned
    const isAutomation = experienceStore.isAutomation
    if (hasRepAssigned || isAutomation) {
      return true
    }

    // check if borrower is disqualified by credit score
    const creditScore = parseInt(get(borrowerValues, 'creditScore.value'))
    if (creditScore < QUALIFIED_CREDIT_THRESHOLD) {
      return false
    }

    // check if borrower is disqualified for revenue being too low
    const averageMonthlySales = parseInt(
      get(borrowerValues, 'average_monthly_sales.value')
    )
    return averageMonthlySales >= QUALIFIED_REVENUE_THRESHOLD
  })

  const getModalContent = computed(() => {
    return (searchParam) => {
      const borrowerValues = get(
        borrowerStore,
        'borrowerValues'
      )
      const hasOpted = get(borrowerValues, 'marketingSmsOptIn.value')
      /// if they have NOT been asked previously
      if (hasOpted === undefined || (hasOpted !== '0' && hasOpted !== '1')) {
        return modalContent['smsOptIn']
      }
      if (window) {
        const param = localStorageService.getItem('portalModal')
        if (param === searchParam) {
          if (param === 'appComplete') {
            return isQualifiedBorrower.value
              ? modalContent['offers']
              : modalContent['dnq']
          }
          return modalContent[param]
        }
      }
      return false
    }
  })

  const creditChallenged = computed(() => {
    const borrowerValues = get(borrowerStore, 'borrowerValues')
    const creditScore = get(borrowerValues, 'creditScore.value')
    if (!creditScore || creditScore < 600) {
      return true
    }
    return false
  })

  const getWywLinks = computed(() => {
    const navUrls = Object.values(PORTAL_NAV_URLS)
    let wywCopy = [...wywLinks.value]
    return wywCopy.filter((link) => {
      const isNav = navUrls.find(
        (navUrl) => link.url === navUrl
      )
      const isCurrentNavLink = navLinks.value.find((nav) => nav.url === isNav)
      return !isNav || isCurrentNavLink
    })
  })

  const hideExploreTab = computed(() => {
    if (!affiliateCustomizationStore.isLendioTenant) {
      return true;
    }

    const isCoreOrPrime = get(borrowerStore, 'isCoreOrPrime')
    const hasFundedDeals = get(dealsStore, 'hasFundedTierOneDeals')
    const tierThreeOptionAvailable = get(dealsStore, 'normalizedEligibleOffers.length') > 0

    return isCoreOrPrime && !hasFundedDeals && !tierThreeOptionAvailable
  })

  const isOptionsPageUpdated = computed(() => {
    return affiliateCustomizationStore.isLendioTenant
      && ((progressCardStore.fundingSteps === 'OFFERS_PRESENTED' && dealsStore.availableOptions?.length)
        || progressCardStore.fundingSteps === 'CLOSING_REQUIREMENTS')
      && approvalsStore.approvalsLoaded
      && dealsStore.loaded
      && offerOptionsStore.loaded
      && dealsStore.isFinancingOptionHashUpToDate === false
  })

  const getNavLinks = computed(() => {
    let navLinkCopy = [...navLinks.value]

    if (isOptionsPageUpdated.value) {
      navLinkCopy = navLinkCopy.map(
        (nav) => nav.title === PORTAL_NAV_TITLES.FINANCING_OPTIONS ? { ...nav, infoBadge: true } : nav
      )
    }

    // Filter unavailable routes
    if (creditChallenged.value) {
      navLinkCopy = navLinkCopy.filter(
        (nav) => nav.title !== PORTAL_NAV_TITLES.CREDIT_CARDS
      )
    }
    const marketplaceAppCompleted = get(borrowerStore, 'borrowerValues.marketplaceAppCompleted.value')
    if (!isQualifiedBorrower.value && !marketplaceAppCompleted) {
      navLinkCopy = navLinkCopy.filter(
        (nav) => nav.title !== PORTAL_NAV_TITLES.FINANCING_OPTIONS
      )
    }

    if (hideExploreTab.value) {
      navLinkCopy = navLinkCopy.filter(
        (nav) => nav.title !== PORTAL_NAV_TITLES.EXPLORE
      )
    }

    const borrower = get(borrowerStore, 'borrower')
    if (!borrower) {
      navLinkCopy = navLinkCopy.filter(
        (nav) => nav.title !== PORTAL_NAV_TITLES.SUNRISE
      )
    }
    return navLinkCopy
  })
  const getEmbeddedNavLinks = computed(() => {
    let embeddedNavLinksCopy = [...embeddedNavLinks.value]
    return embeddedNavLinksCopy;
  })

  const getIntelligentLendingNavLinks = computed(() => {
    let intelligentLendingNavLinksCopy = [...intelligentLendingNavLinks.value]
    return intelligentLendingNavLinksCopy.filter((link) => (
      link.title !== INTELLIGENT_LENDING_PORTAL_NAV_TITLES.DASHBOARD
    ))
  })

  const getBrandedNavLinks = computed(() => {
    return [...brandedNavLinks.value];
  })

  const navigationItems = computed(() => {
    switch (affiliateCustomizationStore.affiliateCustomizations?.affiliate?.medium) {
      case AFFILIATE_TYPES.INTELLIGENT_LENDING:
        return getIntelligentLendingNavLinks.value
      case AFFILIATE_TYPES.BRANDED:
        return getBrandedNavLinks.value
      case AFFILIATE_TYPES.EMBEDDED:
        return getEmbeddedNavLinks.value
      default:
        return getNavLinks.value
    }
  })

  const settingsNavigationItem = computed(() => {
    switch (affiliateCustomizationStore.affiliateCustomizations?.affiliate?.medium) {
      case AFFILIATE_TYPES.INTELLIGENT_LENDING:
        return INTELLIGENT_LENDING_NAV_LINKS.find(link => link.title === INTELLIGENT_LENDING_PORTAL_NAV_TITLES.SETTINGS)
      case AFFILIATE_TYPES.BRANDED:
        return BRANDED_NAV_LINKS.find(link => link.title === PORTAL_NAV_TITLES.SETTINGS)
      case AFFILIATE_TYPES.EMBEDDED:
        return null
      default:
        return PORTAL_NAV_LINKS.find(link => link.title === PORTAL_NAV_TITLES.SETTINGS)
    }
  })

  const isPackageReadyActionNeeded = computed(() => {
    if (!progressStore.appCompleted) {
      return false
    }

    if (affiliateCustomizationStore.isLendioTenant && !isQualifiedBorrower.value) {
      return false
    }

    if (!affiliateCustomizationStore.isLendioTenant && dealsStore.deals?.length && !dealsStore.hasActiveDeals) {
      return false
    }

    if (dealsStore.deals?.some(d => d.stage === 'funded')) {
      return false
    }

    if (!approvalsStore.approvalsLoaded || !offerOptionsStore.loaded || dealsStore.availableOptions?.length > 0) {
      return false
    }

    return borrowerStore.borrower?.isPackageReady === false
  })

  const packageReadyNotificationAlert = computed(() => {
    if (!isPackageReadyActionNeeded.value) {
      return null
    }

    const lenderUser = borrowerStore.lenderUser
    const isLendioOrEmbedded = affiliateCustomizationStore.isLendioTenant
      || rootStore.isEmbedded
      || affiliateCustomizationStore.isBrandedAffiliate
    let supportPhone = isLendioOrEmbedded
      ? '855-946-2619'
      : affiliateCustomizationStore.contactInfoPhone

    const repAssigned = lenderUser
      && !lenderUser.isSystemAccount
      && !affiliateCustomizationStore.isIntelligentLendingAffiliate
      && lenderUser.twilioPhone

    if (repAssigned) {
      supportPhone = lenderUser.twilioPhone
    }

    supportPhone = supportPhone ? phone(supportPhone) : null
    if (!supportPhone) {
      return null
    }

    const contact = repAssigned
      ? `your rep <strong>${lenderUser.fullName}</strong> at <strong>${supportPhone}</strong>`
      : `our customer support team at <strong>${supportPhone}</strong>`

    return `We need a few more details to complete your application. Contact ${contact} and we'll get this sorted right away.`
  })

/*
   █████   ██████ ████████ ██  ██████  ███    ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ████   ██ ██
  ███████ ██         ██    ██ ██    ██ ██ ██  ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ██  ██ ██      ██
  ██   ██  ██████    ██    ██  ██████  ██   ████ ███████
  ACTIONS
  ! - - Actions calling other actions in the same store must use `this.actionName(...)`
  ! - - If we do not use `this.actionName` it will not be properly mockable in tests.
  ! - - Computeds and refs will work fine, and should be called directly though.
*/

  function setCurrentDraw(_currentDraw) {
    currentDraw.value = _currentDraw
  }

  function setWhileYouWaitLinks(links) {
    wywLinks.value = links
  }

  async function getReadyCapitalAppNumber(dealId) {
    if (readyCapitalAppNumber.value) return readyCapitalAppNumber.value
    try {
      const response = await $axios.get(
        `${env('bpApiUrl')}/ready-capital/${dealId}/application-number`
      )
      const { appNumber } = response.data.data

      readyCapitalAppNumber.value = appNumber
      return appNumber
    } catch (e) {
      console.error(e)
      return null
    }
  }

  async function fetchResumeSBAAppLink() {
    if (!shouldFetchResumeSBAUrl.value) {
      return
    }
    // Clear existing value
    sbaResumeUrl.value = ''

    const resumeUrl = await appConfigStore.getResumeApplicationData('resumeSbaApp')
    const portalRoutes = ['/portal', '/app-complete-landing']

    if (resumeUrl && !portalRoutes.some(route => resumeUrl.includes(route))) {
      sbaResumeUrl.value = resumeUrl
    }
    shouldFetchResumeSBAUrl.value = false
  }

  async function refetchAdditionalOwnersAccessCardData() {
    shouldGetOwnersAccessCardData.value = true
    await getAdditionalOwnersAccessCardData()
  }

  async function getAdditionalOwnersAccessCardData() {
    if (!shouldGetOwnersAccessCardData.value) {
      return
    }

    const borrower = await borrowerStore.getBorrower()
    const borrowerId = borrower?.id

    if (!borrowerId) {
      return
    }

    const { data } = await contactsService.getAdditionalOwnersAccessCardData({ borrowerId })

    additionalOwnerData.value = data?.data
    const hasContacts = (additionalOwnerData.value?.contacts || []).length > 0
    shouldDisplayAdditionalOwnersAccessCard.value = !!data?.display && hasContacts
    shouldGetOwnersAccessCardData.value = false
  }

  async function refetchAdditionalOwnersClosingDocumentsData() {
    shouldGetOwnersClosingDocumentsCardData.value = true
    await getAdditionalOwnersClosingDocumentsCardData()
  }

  async function getAdditionalOwnersClosingDocumentsCardData() {
    if (!shouldGetOwnersClosingDocumentsCardData.value) {
      return
    }

    const borrower = await borrowerStore.getBorrower()
    const borrowerId = borrower?.id
    if (!borrowerId) {
      return
    }
    const { data } = await contactsService.getAdditionalOwnersClosingDocumentsCardData({ borrowerId })

    additionalOwnersClosingDocumentsData.value = data?.data
    shouldDisplayAdditionalOwnersClosingDocumentsCard.value = !!data?.display
    shouldGetOwnersClosingDocumentsCardData.value = false
  }

  function invalidateResumeSBALink() {
    shouldFetchResumeSBAUrl.value = true
  }

  function openAlertBanner(type, options = {}) {
    alertBannerType.value = type
    alertBannerMessage.value = options.message ?? null
    alertBannerTitle.value = options.title ?? null
    alertBannerCloseable.value = options.closeable ?? false
    alertBannerShowIcon.value = options.showIcon ?? true
    alertBannerShowShadow.value = options.showShadow ?? false
    alertBannerLink.value = options.link ?? null
    alertBannerLinkText.value = options.linkText ?? null
    alertBannerLabel.value = options.label ?? null
    showAlertBanner.value = true
  }

  function closeAlertBanner(label) {
    if (label && alertBannerLabel.value !== label) return
    showAlertBanner.value = false
    alertBannerLabel.value = null
  }

  async function detectPackageReadyAction() {
    if (!progressStore.sortedApplications?.length) {
      await progressStore.fetchApplications()
    }
    if (!progressStore.appCompleted) return

    const DELAY = 16 * 60 // 16 minutes in seconds to show only after finicity banner has a chance to fire first
    const now = Math.floor(Date.now() / 1000)
    const elapsed = now - progressStore.appCompleted
    const remaining = DELAY - elapsed

    if (remaining <= 0) return

    await new Promise(resolve => setTimeout(resolve, remaining * 1000))
    await borrowerStore.getBorrower({ forceReload: true })
  }

  function reset() {
    readyCapitalAppNumber.value = null
    portalLoaded.value = false
    sbaResumeUrl.value = ''
    shouldFetchResumeSBAUrl.value = true
    shouldGetOwnersAccessCardData.value = true
    additionalOwnerData.value = null
    shouldDisplayAdditionalOwnersAccessCard.value = false
    shouldGetOwnersClosingDocumentsCardData.value = true
    additionalOwnersClosingDocumentsData.value = null
    shouldDisplayAdditionalOwnersClosingDocumentsCard.value = false
    showAlertBanner.value = false
    alertBannerType.value = 'info'
    alertBannerMessage.value = null
    alertBannerTitle.value = null
    alertBannerCloseable.value = false
    alertBannerShowIcon.value = true
    alertBannerShowShadow.value = false
    alertBannerLink.value = null
    alertBannerLinkText.value = null
  }

  return {
    // STATE
    actionItems,
    actionItemsTest,
    additionalOwnerData,
    additionalOwnersClosingDocumentsData,
    showAlertBanner,
    alertBannerType,
    alertBannerMessage,
    alertBannerTitle,
    alertBannerCloseable,
    alertBannerShowIcon,
    alertBannerShowShadow,
    alertBannerLink,
    alertBannerLinkText,
    alertBannerLabel,
    currentDraw,
    dnqCta,
    embeddedNavLinks,
    leadSourceRestricted,
    navLinks,
    partners,
    portalLoaded,
    readyCapitalAppNumber,
    sbaResumeUrl,
    shouldDisplayAdditionalOwnersAccessCard,
    shouldDisplayAdditionalOwnersClosingDocumentsCard,
    shouldGetOwnersAccessCardData,
    shouldGetOwnersClosingDocumentsCardData,
    wywLinks,
    // GETTERS
    creditChallenged,
    getBrandedNavLinks,
    getEmbeddedNavLinks,
    getIntelligentLendingNavLinks,
    getModalContent,
    getNavLinks,
    getWywLinks,
    hideExploreTab,
    isOptionsPageUpdated,
    isQualifiedBorrower,
    navigationItems,
    partnerByName,
    settingsNavigationItem,
    showDNQCta,
    isPackageReadyActionNeeded,
    packageReadyNotificationAlert,

    // ACTIONS
    closeAlertBanner,
    fetchResumeSBAAppLink,
    getAdditionalOwnersAccessCardData,
    getAdditionalOwnersClosingDocumentsCardData,
    getReadyCapitalAppNumber,
    invalidateResumeSBALink,
    openAlertBanner,
    refetchAdditionalOwnersAccessCardData,
    refetchAdditionalOwnersClosingDocumentsData,
    reset,
    setCurrentDraw,
    setWhileYouWaitLinks,
    detectPackageReadyAction,
  }
})
